/**
 * /knowledge-base/new
 * /knowledge-base/:id/edit
 *
 * Create / edit a KB article with TipTap. Manager+ only.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Save, Send, Trash2, History, RotateCcw, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import TipTapEditor from "@/components/kb/TipTapEditor";

const EMPTY_FORM = {
  title: "",
  summary: "",
  body_md: "",
  category_id: "",
  category: "",
  tags: [],
  status: "draft",
  cover_image_url: "",
};

export default function KnowledgeBaseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(EMPTY_FORM);
  const [tagDraft, setTagDraft] = useState("");
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [showHistory, setShowHistory] = useState(false);
  const [revisions, setRevisions] = useState([]);

  useEffect(() => {
    api.get("/kb-categories").then((r) => setCategories(r.data || []));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get(`/kb/${id}`).then((r) => {
      const a = r.data || {};
      setForm({
        title: a.title || "",
        summary: a.summary || "",
        body_md: a.body_md || a.content || "",
        category_id: a.category_id || "",
        category: a.category || "",
        tags: a.tags || [],
        status: a.status || "draft",
        cover_image_url: a.cover_image_url || "",
      });
    }).catch(() => toast.error("Failed to load article")).finally(() => setLoading(false));
  }, [id, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = (raw) => {
    const t = (raw || "").trim().toLowerCase();
    if (!t || form.tags.includes(t)) { setTagDraft(""); return; }
    setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagDraft("");
  };
  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const onCategoryChange = (val) => {
    const cat = categories.find((c) => c.id === val);
    setForm((f) => ({ ...f, category_id: val, category: cat?.name || "" }));
  };

  const flatCats = useMemo(() => labelTree(categories), [categories]);

  const submit = async (asStatus) => {
    if (!form.title.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      const payload = { ...form, status: asStatus };
      const r = isEdit ? await api.patch(`/kb/${id}`, payload) : await api.post("/kb", payload);
      toast.success(isEdit ? "Saved" : asStatus === "published" ? "Published" : "Saved as draft");
      navigate(`/knowledge-base/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const archive = async () => {
    if (!isEdit) return;
    if (!window.confirm("Archive this article? It will be hidden from the default list but kept for audit.")) return;
    try {
      await api.delete(`/kb/${id}`);
      toast.success("Archived");
      navigate("/knowledge-base");
    } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
  };

  const openHistory = async () => {
    setShowHistory(true);
    try {
      const r = await api.get(`/kb/${id}/revisions`);
      setRevisions(r.data || []);
    } catch (e) { toast.error("Failed to load revisions"); }
  };

  const restore = async (revId) => {
    if (!window.confirm("Restore this version? Current content will be replaced (a backup revision is kept).")) return;
    try {
      const r = await api.post(`/kb/${id}/restore/${revId}`);
      toast.success("Restored");
      setForm((f) => ({ ...f, title: r.data.title, summary: r.data.summary || "", body_md: r.data.body_md || "", tags: r.data.tags || [] }));
      setShowHistory(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  if (loading) return <div className="text-sm text-slate-400 py-12 text-center">Loading article…</div>;

  return (
    <div className="space-y-4" data-testid="kb-editor-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/knowledge-base" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Knowledge Base
        </Link>
        <div className="flex items-center gap-2">
          {isEdit && (
            <Button variant="outline" onClick={openHistory} data-testid="kb-history-btn">
              <History className="w-3.5 h-3.5 mr-1.5" /> Version history
            </Button>
          )}
          {isEdit && (
            <Button variant="outline" onClick={archive} className="text-rose-600 hover:text-rose-700"
                    data-testid="kb-archive-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Archive
            </Button>
          )}
          <Button variant="outline" onClick={() => submit("draft")} disabled={saving}
                  data-testid="kb-save-draft-btn">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save draft"}
          </Button>
          <Button onClick={() => submit("published")} disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="kb-publish-btn">
            <Send className="w-3.5 h-3.5 mr-1.5" /> {form.status === "published" ? "Update" : "Publish"}
          </Button>
        </div>
      </div>

      <h1 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-indigo-600" />
        {isEdit ? "Edit article" : "New article"}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* Main */}
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={(e) => setField("title", e.target.value)}
                     placeholder="How to process a refund for a cancelled order"
                     className="text-base font-semibold h-11"
                     data-testid="kb-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Summary (1–2 sentences shown in search results)</Label>
              <Textarea value={form.summary} onChange={(e) => setField("summary", e.target.value)}
                        placeholder="Step-by-step process for an L1 agent to refund a cancelled online order…"
                        rows={2}
                        data-testid="kb-summary-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Body</Label>
              <TipTapEditor value={form.body_md} onChange={(v) => setField("body_md", v)} />
            </div>
          </CardContent>
        </Card>

        {/* Side */}
        <Card className="border-slate-200 h-fit">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase tracking-wider text-slate-500">Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 -mt-1">
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="kb-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft" className="text-xs">Draft</SelectItem>
                  <SelectItem value="published" className="text-xs">Published</SelectItem>
                  <SelectItem value="archived" className="text-xs">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select value={form.category_id || "__none"} onValueChange={(v) => onCategoryChange(v === "__none" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="kb-category-select"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none" className="text-xs">— None —</SelectItem>
                  {flatCats.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-xs">
                      {"  ".repeat(c.depth)}{c.depth > 0 ? "└ " : ""}{c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tags</Label>
              <div className="flex flex-wrap gap-1 mb-1">
                {form.tags.map((t) => (
                  <Badge key={t} variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 gap-1"
                         data-testid={`kb-tag-chip-${t}`}>
                    {t}
                    <button type="button" onClick={() => removeTag(t)} className="text-indigo-400 hover:text-indigo-700">×</button>
                  </Badge>
                ))}
              </div>
              <Input value={tagDraft}
                     onChange={(e) => setTagDraft(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagDraft); } }}
                     onBlur={() => addTag(tagDraft)}
                     placeholder="Add tag and press Enter…"
                     className="h-8 text-xs"
                     data-testid="kb-tag-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cover image URL (optional)</Label>
              <Input value={form.cover_image_url} onChange={(e) => setField("cover_image_url", e.target.value)}
                     placeholder="/api/kb-files/…  (use /api/kb-files/upload)"
                     className="h-8 text-xs font-mono" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Version history dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl" data-testid="kb-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="w-4 h-4 text-indigo-600" /> Version history
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Each save creates a snapshot. Restore any version to roll back (a backup of the current state is kept too).
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-100">
            {revisions.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No revisions yet.</div>}
            {revisions.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-2"
                   data-testid={`kb-rev-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{r.title}</div>
                  <div className="text-[11px] text-slate-500">
                    {new Date(r.created_at).toLocaleString()} · {r.created_by_name || r.created_by} · {r.action}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => restore(r.id)} className="h-8 text-xs"
                        data-testid={`kb-rev-restore-${r.id}`}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function labelTree(rows) {
  const byId = Object.fromEntries(rows.map((r) => [r.id, r]));
  const out = [];
  const visit = (id, depth) => {
    const r = byId[id]; if (!r) return;
    out.push({ ...r, depth });
    rows.filter((x) => x.parent_id === id).forEach((c) => visit(c.id, depth + 1));
  };
  rows.filter((r) => !r.parent_id || !byId[r.parent_id]).forEach((r) => visit(r.id, 0));
  return out;
}
